import { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';

import Card from 'components/card';
import CloseModalButton from 'components/buttons/CloseModalButton';
import CreateButton from 'components/buttons/CreateButton';
import { CustomExportEntity, downloadCustomExport, getCustomExportFields } from 'interfaces/customExport';

const CustomExportModal = (props: { isOpen: boolean; onClose: () => void }) => {
    const { isOpen, onClose } = props;

    const [entities, setEntities] = useState<CustomExportEntity[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
    const [selectedFields, setSelectedFields] = useState<string[]>([]);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        getCustomExportFields().then((res) => {
            setEntities(res || []);
            setSelectedEntity(res?.[0]?.entity || null);
            setSelectedFields([]);
            setLoading(false);
        });
    }, [isOpen]);

    const currentEntity = entities.find((e) => e.entity === selectedEntity);

    const handleSelectEntity = (entity: string) => {
        setSelectedEntity(entity);
        setSelectedFields([]);
    };

    const toggleField = (key: string) => {
        setSelectedFields((prev) =>
            prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
        );
    };

    const toggleAll = () => {
        if (!currentEntity) return;
        const allKeys = currentEntity.fields.map((f) => f.key);
        setSelectedFields(selectedFields.length === allKeys.length ? [] : allKeys);
    };

    const handleExport = async () => {
        if (!selectedEntity || selectedFields.length === 0) return;
        setExporting(true);
        const success = await downloadCustomExport(selectedEntity, selectedFields);
        setExporting(false);
        if (success) onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] mx-auto md:top-[8vh]">
                <ModalBody>
                    <Card extra="px-[30px] sm:ml-[168px] sm:mr-[9px] sm:pt-[35px] pb-[40px] xl:max-w-[80%] xl:max-w-3xl flex !z-[1004] xl:mx-auto">
                        <CloseModalButton onClick={onClose} extra={'w-[34px] h-[34px]'} />
                        <h1 className="text-2xl font-bold text-[#2B3674]">Export personnalisé</h1>
                        <p className="mt-2 text-sm text-gray-600">
                            Choisissez une entité puis les colonnes à inclure dans le fichier CSV.
                        </p>

                        {loading ? (
                            <p className="mt-6 text-sm text-gray-500">Chargement...</p>
                        ) : (
                            <>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {entities.map((e) => (
                                        <button
                                            key={e.entity}
                                            type="button"
                                            onClick={() => handleSelectEntity(e.entity)}
                                            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                                                selectedEntity === e.entity
                                                    ? 'bg-toola-500 text-white'
                                                    : 'bg-lightPrimary text-navy-700'
                                            }`}
                                        >
                                            {e.label}
                                        </button>
                                    ))}
                                </div>

                                {currentEntity && (
                                    <div className="mt-5">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                Champs ({selectedFields.length}/{currentEntity.fields.length})
                                            </p>
                                            <button
                                                type="button"
                                                onClick={toggleAll}
                                                className="text-xs font-medium text-toola-500 hover:underline"
                                            >
                                                {selectedFields.length === currentEntity.fields.length
                                                    ? 'Tout désélectionner'
                                                    : 'Tout sélectionner'}
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
                                            {currentEntity.fields.map((field) => (
                                                <label key={field.key} className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedFields.includes(field.key)}
                                                        onChange={() => toggleField(field.key)}
                                                        className="mr-2 h-4 w-4 text-toola-500 focus:ring-toola-500 border-gray-300 rounded"
                                                    />
                                                    <span className="text-sm text-gray-700">{field.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="mt-6 flex justify-end">
                                    <CreateButton
                                        value={exporting ? 'Export en cours...' : 'Exporter'}
                                        extra="text-sm px-5 h-10 w-full sm:w-auto mb-0 bg-toola-500"
                                        onClick={handleExport}
                                        disabled={exporting || selectedFields.length === 0}
                                    />
                                </div>
                            </>
                        )}
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default CustomExportModal;
